import DownIcons from '@/icons/DownIcons'
import FolderIcons from '@/icons/FolderIcons/FolderIcons'
import {
    GetObjectPermissionLevel,
    ListObject,
    RenameObject,
} from '@/modules/Drive/services'
import {
    extractExtension,
    extractName,
    FileIcon,
    getParentDir,
    getFolderName,
    popPath,
} from '@/modules/Drive/utils'
import useAuthStore from '@/stores/useAuthStore'
import { ModalProps } from '@/types/shared'
import { useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'
import { useCallback, useMemo, useState } from 'react'
import { Button, Dialog, Loading } from 'vnetwork-ui'
import { fetchStarredObjectKeys } from '@/modules/Drive/services/StarredObject'
import toast from 'react-hot-toast'
import useDriveStore from '@/stores/useDriveStore'
import ModifyObjectForm from '../ModifyObjectModal/ModifyObjectForm'
import { ObjectType } from '@/modules/Drive/types'
import { checkPermission } from '@/modules/Drive/utils/DrivePermission/permission'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface MoveObjectProps extends ModalProps {
    objectName: string
    objectKey: string
    objectType?: ObjectType
}

const MoveObjectModal: React.FC<MoveObjectProps> = ({
    objectName,
    objectKey,
    objectType,
    open,
    onClose,
}) => {
    const queryClient = useQueryClient()
    const uuid = useAuthStore((state) => state.employee?.employee_uuid)
    const employee = useAuthStore((state) => state.employee)
    const setStarredObjectKeys = useDriveStore(
        (state) => state.setStarredObjectKeys
    )
    const parentDir = getParentDir(objectKey)
    const [selectedFolder, setSelectedFolder] = useState<string>(parentDir)
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const isUuidV4 = (value?: string) =>
        !!value &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
            value
        )

    const isRoot = isUuidV4(extractName(selectedFolder))

    const currentPrefix = isRoot
        ? t('my-drive-title')
        : getFolderName(selectedFolder) || extractName(selectedFolder)

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.DRIVE_FOLDER_EXPLORER, selectedFolder],
        queryFn: () => ListObject(selectedFolder),
        staleTime: 0,
    })

    const parentPath = useMemo(
        () => (selectedFolder ? popPath(selectedFolder) : ''),
        [selectedFolder]
    )

    // Current folder permission can be added later if needed

    const pathOwnedByEmployee = useCallback(
        (path?: string) => {
            if (!path || !employee?.employee_uuid) return false
            return path.split('/').includes(employee.employee_uuid)
        },
        [employee?.employee_uuid]
    )

    const { data: parentPermission, isFetching: isFetchingParentPermission } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.DRIVE_OBJECT_PERMISSION, parentPath],
            queryFn: () =>
                GetObjectPermissionLevel(
                    parentPath || '',
                    employee?.employee_id,
                    ObjectType.FOLDER
                ),
            enabled:
                !!parentPath &&
                !!employee?.employee_id &&
                !pathOwnedByEmployee(parentPath),
            staleTime: 5 * 60 * 1000,
        })

    const canViewParent = useMemo(() => {
        if (pathOwnedByEmployee(parentPath)) return true
        const level = parentPermission?.result?.share_permission_level
        return level ? checkPermission(level, 'view') : false
    }, [parentPermission, parentPath, pathOwnedByEmployee])

    const { mutateAsync: moveObjectMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: RenameObject },
            {
                reactQueryKeys: [QUERY_KEYS.DRIVE],
                successMessage: t('move-success'),
                onSuccess: async () => {
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.DRIVE_STARRED_LIST],
                    })
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.DRIVE_RECENT],
                    })
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.DRIVE_FOLDER_EXPLORER],
                    })
                    queryClient.invalidateQueries({
                        queryKey: [QUERY_KEYS.DRIVE],
                    })

                    if (uuid) {
                        const updatedKeys = await fetchStarredObjectKeys(uuid)
                        setStarredObjectKeys(updatedKeys)
                    }
                    onClose()
                },
                onError: (error: any) => {
                    const errorMessage =
                        error?.response?.data?.error_description ||
                        t('something-wrong')
                    switch (error?.response?.data?.error) {
                        case '409':
                            toast.error(
                                objectType === ObjectType.FOLDER
                                    ? t('folder-existed')
                                    : t('file-existed')
                            )
                            break
                        case '403':
                            toast.error(t('drive.no-modify-permission'))
                            break
                        default:
                            toast.error(errorMessage)
                            break
                    }
                },
            }
        )

    const folders = data?.result.folders
    const files = data?.result.files
    const isEmpty = !folders && files?.length === 0

    const handleFolderClick = useCallback(
        async (folderPrefix: string) => {
            if (!pathOwnedByEmployee(folderPrefix)) {
                try {
                    const permission = await GetObjectPermissionLevel(
                        folderPrefix,
                        employee?.employee_id,
                        ObjectType.FOLDER
                    )
                    const level = permission.result.share_permission_level
                    if (!checkPermission(level, 'view')) {
                        toast.error(t('drive.no-view-permission'))
                        return
                    }
                } catch (error: any) {
                    console.error(error)
                    toast.error(t('something-wrong'))
                    return
                }
            }
            setSelectedFolder(folderPrefix)
        },
        [employee?.employee_id, pathOwnedByEmployee]
    )

    const handleMoveObject = async (
        objectKey: string,
        objectName: string,
        targetFolderKey: string
    ) => {
        const normalizedTargetKey = targetFolderKey.endsWith('/')
            ? targetFolderKey
            : `${targetFolderKey}/`
        const trimmedKey = objectKey.trim().replace(/\/+$/, '')

        const isDuplicate =
            files?.some((file) => file.Key === trimmedKey) ||
            folders?.some((folder) => folder.Prefix === trimmedKey)

        if (isDuplicate) {
            toast.error(t('folder-existed'))
            return
        }

        await moveObjectMutation({
            old_object_key: objectKey,
            new_object_key: normalizedTargetKey + objectName,
        })
    }

    const renderFolders = useMemo(() => {
        if (!folders) return null

        return folders.map((folder) => (
            <div
                key={folder.Prefix}
                className={`flex items-center justify-between p-2 rounded-md hover:bg-gray-100 cursor-pointer ${
                    folder.Prefix === objectKey
                        ? 'opacity-50 pointer-events-none'
                        : ''
                }`}
                onClick={() => handleFolderClick(folder.Prefix)}
            >
                <div className="flex items-center gap-4">
                    <div className="size-5">
                        <FolderIcons />
                    </div>
                    <p className="text-sm truncate">
                        {getFolderName(folder.Prefix) ||
                            extractName(folder.Prefix)}
                    </p>
                </div>
            </div>
        ))
    }, [folders, objectKey, handleFolderClick])

    const renderFiles = useMemo(() => {
        if (!files) return null

        return files.map((file) => (
            <div
                key={file.Key}
                className="flex items-center justify-between p-2 rounded-md hover:bg-gray-100 opacity-50"
            >
                <div className="max-w-[100%] flex items-center gap-4 flex-1">
                    <div className="size-5 shrink-0">
                        {FileIcon[extractExtension(file.Key)]}
                    </div>
                    <p className="text-sm truncate">{extractName(file.Key)}</p>
                </div>
            </div>
        ))
    }, [files])

    const modalEmpty = (
        <div className="flex flex-col gap-4 h-[60vh]">
            <div
                className={`w-full py-3 flex flex-col gap-1 ${isOpen ? 'bg-gray-300' : 'bg-gray-50'} items-center`}
            >
                <p className="text-center text-gray-400 font-bold">
                    {t('empty-folder')}
                </p>
            </div>
        </div>
    )

    const stateComponent = renderState()

    const modalContent = (
        <div className={`h-[60vh] ${isOpen && 'pointer-events-none'}`}>
            {stateComponent ? (
                <div className="p-4">{stateComponent}</div>
            ) : (
                <div>
                    {renderFolders}
                    {renderFiles}
                </div>
            )}
        </div>
    )

    const modalNavigation = (
        <div className="flex space-x-2">
            {!isRoot && (
                <div
                    className={`flex h-full aspect-square items-center justify-center rounded-full ${
                        isFetchingParentPermission
                            ? 'opacity-50'
                            : canViewParent
                              ? 'hover:bg-gray-100'
                              : 'opacity-50'
                    }`}
                    onClick={async () => {
                        if (isFetchingParentPermission) return
                        if (!canViewParent) {
                            toast.error(t('drive.no-view-permission'))
                            return
                        }
                        setSelectedFolder(popPath(selectedFolder ?? ''))
                    }}
                >
                    {isFetchingParentPermission ? (
                        <Loading.CircularProgress width={16} height={16} />
                    ) : (
                        <DownIcons
                            className={`size-5 -rotate-90 ${
                                canViewParent
                                    ? 'cursor-pointer'
                                    : 'cursor-not-allowed'
                            }`}
                        />
                    )}
                </div>
            )}
            <div>{currentPrefix}</div>
        </div>
    )

    const modalCreateFolder = isOpen && (
        <div className="w-full absolute bottom-0 left-0 bg-white rounded-lg p-6 z-[100] animate-slide-up">
            <ModifyObjectForm
                onClose={() => setIsOpen(false)}
                placeholder={t('create-folder-title')}
                createPrefix={selectedFolder}
                type={ObjectType.FOLDER}
            />
        </div>
    )

    const modalActions = (
        <div className="w-full flex flex-col space-y-2">
            {modalCreateFolder}
            {modalNavigation}
            <div className="w-full flex items-center justify-between">
                <div>
                    <Button
                        variant="outlined"
                        disabled={isPending}
                        onClick={() => setIsOpen(true)}
                    >
                        {t('create-folder-item')}
                    </Button>
                </div>
                <div className="flex space-x-2">
                    <Button onClick={onClose} variant="outlined">
                        {t('cancel')}
                    </Button>
                    <Button
                        disabled={
                            isPending ||
                            parentDir === selectedFolder ||
                            isPending
                        }
                        loading={isPending}
                        onClick={() =>
                            handleMoveObject(
                                objectKey,
                                objectName,
                                selectedFolder
                            )
                        }
                    >
                        {t('move')}
                    </Button>
                </div>
            </div>
        </div>
    )

    return (
        <Dialog
            onClose={onClose}
            open={open}
            title={`Di chuyển "${objectName}"`}
            children={isEmpty ? modalEmpty : modalContent}
            actions={modalActions}
            className={isOpen ? 'bg-gray-300' : ''}
        />
    )
}

export default MoveObjectModal
